import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  fetchRegistrationDetail,
  fetchStepSummary,
  updateStepStatus,
  updateStepProgress,
  submitStepWork,
  type RegistrationDetail,
  type StepProgress,
  type StepSummary,
  type SessionDay,
} from '../../api/learnPublicApi';
import {
  CalendarBlank, CaretLeft, CheckCircle, HourglassSimple,
  XCircle, WarningCircle, Play, BookOpen, Eye,
  Circle, SkipForward, Timer, FileText, VideoCamera, Exam,
  ListChecks, ArrowClockwise, PaperPlaneTilt, LinkSimple,
  MapPin, Clock, Monitor, Buildings, DownloadSimple, X,
} from '@phosphor-icons/react';
import { useTraining } from './TrainingContext';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Progress,
  Spinner,
  Input,
  Textarea,
  Label,
} from '@t-one-internal/design-system';

// ─── Status config ─────────────────────────────────────────────────────────

type StepStatusKey = 'completed' | 'skipped' | 'in_progress' | 'failed' | 'not_started';

const STATUS_CFG: Record<StepStatusKey, {
  label: string;
  cardBorder: string;
  cardBg: string;
  nodeBg: string;
  nodeBorder: string;
  connectorBg: string;
  badgeVariant: 'default' | 'secondary' | 'destructive' | 'outline';
  badgeCls: string;
  numberCls: string;
  nodeIcon: typeof CheckCircle | null;
}> = {
  completed: {
    label: 'Completed',
    cardBorder: 'border-l-emerald-500',
    cardBg: 'bg-emerald-50/30',
    nodeBg: 'bg-emerald-500',
    nodeBorder: 'border-emerald-500',
    connectorBg: 'bg-emerald-300',
    badgeVariant: 'default',
    badgeCls: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50',
    numberCls: 'bg-emerald-100 text-emerald-700',
    nodeIcon: CheckCircle,
  },
  in_progress: {
    label: 'In Progress',
    cardBorder: 'border-l-blue-500',
    cardBg: 'bg-blue-50/20',
    nodeBg: 'bg-blue-500',
    nodeBorder: 'border-blue-500',
    connectorBg: 'bg-gray-200',
    badgeVariant: 'default',
    badgeCls: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50',
    numberCls: 'bg-blue-100 text-blue-700',
    nodeIcon: Timer,
  },
  not_started: {
    label: 'Not Started',
    cardBorder: 'border-l-gray-200',
    cardBg: 'bg-white',
    nodeBg: 'bg-white',
    nodeBorder: 'border-gray-300',
    connectorBg: 'bg-gray-200',
    badgeVariant: 'secondary',
    badgeCls: 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-50',
    numberCls: 'bg-gray-100 text-gray-400',
    nodeIcon: null,
  },
  failed: {
    label: 'Failed',
    cardBorder: 'border-l-red-500',
    cardBg: 'bg-red-50/20',
    nodeBg: 'bg-red-500',
    nodeBorder: 'border-red-500',
    connectorBg: 'bg-gray-200',
    badgeVariant: 'destructive',
    badgeCls: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-50',
    numberCls: 'bg-red-100 text-red-700',
    nodeIcon: XCircle,
  },
  skipped: {
    label: 'Skipped',
    cardBorder: 'border-l-amber-400',
    cardBg: 'bg-amber-50/30',
    nodeBg: 'bg-amber-400',
    nodeBorder: 'border-amber-400',
    connectorBg: 'bg-gray-200',
    badgeVariant: 'outline',
    badgeCls: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50',
    numberCls: 'bg-amber-100 text-amber-700',
    nodeIcon: SkipForward,
  },
};

function getStepStatusCfg(status: string) {
  return STATUS_CFG[status as StepStatusKey] || STATUS_CFG.not_started;
}

// ─── Type config ────────────────────────────────────────────────────────────

const TYPE_CLS: Record<string, string> = {
  ilt: 'bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-100',
  preparation: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100',
  assessment: 'bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100',
  task: 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100',
  survey: 'bg-pink-100 text-pink-700 border-pink-200 hover:bg-pink-100',
  video: 'bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100',
  document: 'bg-cyan-100 text-cyan-700 border-cyan-200 hover:bg-cyan-100',
};

const TYPE_ICONS: Record<string, typeof BookOpen> = {
  ilt: VideoCamera,
  preparation: BookOpen,
  assessment: Exam,
  task: ListChecks,
  survey: FileText,
  video: VideoCamera,
  document: FileText,
};

function getTypeCls(type: string) {
  return TYPE_CLS[(type || '').toLowerCase()] ?? 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-100';
}

function getTypeIcon(type: string) {
  return TYPE_ICONS[(type || '').toLowerCase()] ?? BookOpen;
}

// ─── Enrollment status config ───────────────────────────────────────────────

const ENROLLMENT_STATUS: Record<string, { label: string; cls: string; icon: typeof CheckCircle }> = {
  registered: { label: 'Registered', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  attended:   { label: 'Attended', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  accepted:   { label: 'Accepted', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  invited:    { label: 'Invited', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: HourglassSimple },
  waitlisted: { label: 'Waitlisted', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: HourglassSimple },
  cancelled:  { label: 'Cancelled', cls: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
};

function getEnrollmentCfg(status: string) {
  return ENROLLMENT_STATUS[status] || { label: status, cls: 'bg-gray-50 text-gray-600 border-gray-200', icon: Circle };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(dateStr?: string | null) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(timeStr?: string | null) {
  if (!timeStr) return '';
  // Handle "HH:MM:SS" or "HH:MM" or ISO time
  const parts = timeStr.split(':');
  if (parts.length >= 2) {
    const h = parseInt(parts[0], 10);
    const m = parts[1];
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  }
  return timeStr;
}

function getContentUrl(attachment: Record<string, unknown> | null): string | null {
  if (!attachment) return null;
  const url = attachment.url as string;
  if (url) return url;
  const files = attachment.files as Array<{ url?: string }>;
  if (files && files.length > 0 && files[0].url) return files[0].url;
  return null;
}

// ─── Content type detection ─────────────────────────────────────────────────

const CDN_BASE = 'https://newlms-cdn-staging.trainery.one';

function resolveUrl(filePath?: string): string {
  if (!filePath) return '';
  if (filePath.startsWith('http')) return filePath;
  return `${CDN_BASE}/${filePath.replace(/^\/+/, '')}`;
}

interface AttachFile { url: string; name?: string }

function extractAttachments(attachment: Record<string, unknown> | null): AttachFile[] {
  if (!attachment) return [];
  try {
    let data: any = attachment;
    if (typeof data === 'string') {
      if (data.startsWith('http')) return [{ url: data }];
      data = JSON.parse(data);
    }
    if (data?.RawMessage) data = data.RawMessage;
    if (!data || (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0)) return [];
    const toFile = (item: any): AttachFile | null => {
      const u = item?.url || item?.file_url;
      return u ? { url: u, name: item?.name || item?.file_name } : null;
    };
    if (Array.isArray(data?.files)) return data.files.map(toFile).filter(Boolean) as AttachFile[];
    if (Array.isArray(data)) return data.map(toFile).filter(Boolean) as AttachFile[];
    const f = toFile(data);
    return f ? [f] : [];
  } catch { return []; }
}

type ContentType = 'video' | 'audio' | 'pdf' | 'image' | 'document' | 'unknown';

function detectType(filePath?: string): ContentType {
  const path = (filePath || '').toLowerCase();
  const ext = path.split('?')[0].split('.').pop() || '';
  if (['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(ext)) return 'audio';
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
  if (['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'txt'].includes(ext)) return 'document';
  if (path.includes('vimeo.com')) return 'video';
  return 'unknown';
}

// ─── Step Player Modal ──────────────────────────────────────────────────────

function StepPlayerModal({ step, token, onClose, onStepUpdated }: {
  step: StepProgress;
  token: string;
  onClose: () => void;
  onStepUpdated: () => void;
}) {
  const [completed, setCompleted] = useState(step.status === 'completed');
  const [progress, setProgress] = useState(step.progress_percentage || 0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedRef = useRef(step.progress_percentage || 0);

  const attachments = extractAttachments(step.step_attachment);
  const cfg = getStepStatusCfg(step.status);
  const NodeIcon = cfg.nodeIcon;

  const markStarted = useCallback(async () => {
    if (step.status !== 'not_started') return;
    try { await updateStepStatus(step.id, 'in_progress', token); } catch {}
  }, [step.id, step.status, token]);

  const markComplete = useCallback(async () => {
    if (completed) return;
    setCompleted(true);
    try {
      await updateStepStatus(step.id, 'completed', token);
      onStepUpdated();
    } catch { setCompleted(false); }
  }, [completed, step.id, token, onStepUpdated]);

  const saveProgress = useCallback((pct: number) => {
    setProgress(pct);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const rounded = Math.round(pct);
      if (rounded > savedRef.current) {
        savedRef.current = rounded;
        updateStepProgress(step.id, rounded, token).catch(() => {});
      }
    }, 3000);
  }, [step.id, token]);

  const handleTimeUpdate = useCallback((el: HTMLMediaElement) => {
    if (!el.duration) return;
    const pct = (el.currentTime / el.duration) * 100;
    saveProgress(pct);
    if (pct >= 90 && !completed) markComplete();
  }, [saveProgress, completed, markComplete]);

  const handleClose = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const pct = Math.round(progress);
    if (!completed && pct > savedRef.current && pct > 0) {
      updateStepProgress(step.id, pct, token).catch(() => {});
    }
    onClose();
  };

  const renderAttachment = (file: AttachFile, idx: number) => {
    const url = resolveUrl(file.url);
    const type = detectType(file.url);

    if (type === 'video') {
      return (
        <video
          key={idx}
          ref={videoRef}
          src={url}
          controls
          className="w-full rounded-lg"
          style={{ maxHeight: '55vh' }}
          onPlay={markStarted}
          onTimeUpdate={(e) => handleTimeUpdate(e.currentTarget)}
          onEnded={() => { if (!completed) markComplete(); }}
        />
      );
    }
    if (type === 'audio') {
      return (
        <audio
          key={idx}
          ref={audioRef}
          src={url}
          controls
          className="w-full"
          onPlay={markStarted}
          onTimeUpdate={(e) => handleTimeUpdate(e.currentTarget)}
          onEnded={() => { if (!completed) markComplete(); }}
        />
      );
    }
    if (type === 'image') {
      return (
        <div key={idx} className="flex flex-col items-center bg-gray-50 rounded-lg border p-3 gap-2">
          <img src={url} alt={file.name || 'Attachment'} className="max-w-full max-h-[55vh] object-contain rounded" onLoad={markStarted} />
          {file.name && <p className="text-xs text-gray-500 truncate max-w-full">{file.name}</p>}
        </div>
      );
    }
    if (type === 'pdf') {
      return (
        <div key={idx} className="rounded-lg border overflow-hidden" style={{ height: '60vh' }}>
          <iframe src={url} title={file.name || 'PDF'} className="w-full h-full" onLoad={() => { markStarted(); saveProgress(50); }} />
        </div>
      );
    }
    // Fallback: download link
    return (
      <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 border rounded text-sm text-blue-600 hover:bg-blue-50 no-underline">
        <DownloadSimple size={16} />
        {file.name || 'Download file'}
      </a>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999]" onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b flex items-center justify-between shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-gray-900 truncate">{step.step_name || 'Step'}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              {step.step_type && (
                <Badge variant="outline" className={getTypeCls(step.step_type)}>
                  {step.step_type}
                </Badge>
              )}
              {completed ? (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                  <CheckCircle size={12} weight="fill" className="mr-1" /> Completed
                </Badge>
              ) : (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Progress value={progress} className="w-20 h-1.5" />
                  {Math.round(progress)}%
                </div>
              )}
            </div>
          </div>
          <button onClick={handleClose} className="p-1.5 hover:bg-gray-100 rounded-lg ml-3 bg-transparent border-none cursor-pointer">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {step.step_description && (
            <div className="px-5 py-2.5 bg-gray-50 border-b text-sm text-gray-700">{step.step_description}</div>
          )}
          <div className="p-4 space-y-4">
            {attachments.length > 0 ? (
              attachments.map((file, idx) => renderAttachment(file, idx))
            ) : (
              <p className="text-sm text-gray-500 text-center py-6">
                {step.step_description ? 'Review the instructions above and mark as complete when done.' : 'No content attached. Mark as complete when ready.'}
              </p>
            )}

            {completed ? (
              <div className="flex items-center justify-center gap-2 pt-2 text-emerald-600 text-sm font-medium">
                <CheckCircle size={18} weight="fill" /> Completed
              </div>
            ) : (
              <div className="flex justify-center pt-2">
                <Button onClick={markComplete} className="bg-indigo-600 hover:bg-indigo-700">
                  Mark as Complete
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t bg-gray-50 flex items-center justify-between shrink-0 rounded-b-xl">
          <div className="text-xs text-gray-400">
            {step.step_due_date && <span>Due: {formatDate(step.step_due_date)}</span>}
          </div>
          <Button variant="outline" size="sm" onClick={handleClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

function getModeLabel(mode?: string) {
  if (!mode) return '';
  const m = mode.toLowerCase();
  if (m === 'virtual' || m === 'online') return 'Virtual';
  if (m === 'in_person' || m === 'in-person' || m === 'physical') return 'In Person';
  if (m === 'hybrid') return 'Hybrid';
  return mode;
}

// ─── Section divider ───────────────────────────────────────────────────────

function SectionLabel({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="flex-1 h-px bg-gray-200" />
      <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 whitespace-nowrap">
        {label} ({count})
      </span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
}

// ─── Attendance Day Card ────────────────────────────────────────────────────

const ATTENDANCE_CFG: Record<string, { label: string; cls: string }> = {
  attended:  { label: 'Attended', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50' },
  absent:    { label: 'Absent', cls: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-50' },
  pending:   { label: 'Pending', cls: 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-50' },
  excused:   { label: 'Excused', cls: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50' },
};

function SessionDayCard({ day, index }: { day: SessionDay; index: number }) {
  const attCfg = ATTENDANCE_CFG[day.attendance_status || 'pending'] || ATTENDANCE_CFG.pending;
  const dayNum = day.day_number ?? index + 1;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarBlank size={18} weight="duotone" className="text-indigo-400 flex-shrink-0" />
          <div>
            <span className="text-sm font-semibold text-gray-900">
              Day {dayNum} &mdash; {formatDate(day.day_date)}
            </span>
            {(day.start_time || day.end_time) && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                <Clock size={12} weight="bold" className="text-gray-400" />
                {formatTime(day.start_time)}
                {day.end_time && <> &ndash; {formatTime(day.end_time)}</>}
              </div>
            )}
            {day.ilt_platform && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                <Monitor size={12} weight="bold" className="text-indigo-400" />
                <span className="text-indigo-600 font-medium">{day.ilt_platform.toUpperCase()}</span>
                {day.ilt_meeting_status && (
                  <span className="text-gray-400">&middot; {day.ilt_meeting_status}</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {day.attendance_status && (
            <Badge variant="outline" className={attCfg.cls}>
              {attCfg.label}
            </Badge>
          )}
          {day.ilt_join_url && (
            <a href={day.ilt_join_url} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50">
                <Monitor size={13} weight="bold" className="mr-1" />
                Join
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function TrainingLearnerPage() {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const { magicToken, catalogPath, setPageTitle } = useTraining();

  const [detail, setDetail] = useState<RegistrationDetail | null>(null);
  const [summary, setSummary] = useState<StepSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStep, setUpdatingStep] = useState<number | null>(null);

  useEffect(() => { setPageTitle('Course Progress'); }, [setPageTitle]);

  // Step player modal
  const [playerStep, setPlayerStep] = useState<StepProgress | null>(null);

  // Submit work dialog
  const [submitModal, setSubmitModal] = useState<{ progressId: number; stepName: string } | null>(null);
  const [submitUrl, setSubmitUrl] = useState('');
  const [submitNotes, setSubmitNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    if (!magicToken || !enrollmentId) return;
    const eid = parseInt(enrollmentId, 10);
    if (isNaN(eid)) return;
    const [detailData, summaryData] = await Promise.all([
      fetchRegistrationDetail(eid, magicToken),
      fetchStepSummary(eid, magicToken).catch(() => null),
    ]);
    setDetail(detailData);
    setSummary(summaryData);
  }, [magicToken, enrollmentId]);

  useEffect(() => {
    if (!magicToken) { setError('Access denied. A valid portal token is required.'); setLoading(false); return; }
    if (!enrollmentId) { setError('No registration specified.'); setLoading(false); return; }
    loadData()
      .catch(() => setError('Could not load registration details. The link may be invalid or expired.'))
      .finally(() => setLoading(false));
  }, [magicToken, enrollmentId, loadData]);

  // ── Step actions ──

  const handleStartStep = async (progressId: number) => {
    setUpdatingStep(progressId);
    try { await updateStepStatus(progressId, 'in_progress', magicToken); await loadData(); }
    catch (err) { console.error('Failed to start step:', err); }
    finally { setUpdatingStep(null); }
  };

  const handleCompleteStep = async (progressId: number) => {
    setUpdatingStep(progressId);
    try { await updateStepStatus(progressId, 'completed', magicToken); await loadData(); }
    catch (err) { console.error('Failed to complete step:', err); }
    finally { setUpdatingStep(null); }
  };

  const handleOpenContent = (step: StepProgress) => {
    setPlayerStep(step);
  };

  const handleSubmitWork = async () => {
    if (!submitModal || !submitUrl.trim()) return;
    setSubmitting(true);
    try {
      await submitStepWork(submitModal.progressId, submitUrl.trim(), submitNotes.trim() ? { notes: submitNotes.trim() } : null, magicToken);
      setSubmitModal(null); setSubmitUrl(''); setSubmitNotes('');
      await loadData();
    } catch (err) { console.error('Failed to submit work:', err); }
    finally { setSubmitting(false); }
  };

  // ── Loading ──

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Spinner className="h-8 w-8 text-indigo-600" />
      </div>
    );
  }

  // ── Error ──

  if (error || !detail) {
    return (
      <div className="flex items-center justify-center px-6 py-16">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <WarningCircle size={28} weight="duotone" className="text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-sm text-gray-500 leading-relaxed mb-6">{error || 'Registration not found.'}</p>
          {magicToken ? (
            <Link to={`/training/portal?t=${magicToken}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 no-underline">
              <CaretLeft size={14} weight="bold" /> Back to My Registrations
            </Link>
          ) : (
            <Link to={catalogPath} className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 no-underline">
              Browse Catalog
            </Link>
          )}
        </div>
      </div>
    );
  }

  const { enrollment, steps } = detail;
  const sortedSteps = [...steps].sort((a, b) => (a.step_order ?? 999) - (b.step_order ?? 999));
  const enrollmentCfg = getEnrollmentCfg(enrollment.enrollment_status);
  const EnrollmentIcon = enrollmentCfg.icon;

  const totalSteps = sortedSteps.length;
  const completedCount = summary?.completed_steps ?? sortedSteps.filter((s) => s.status === 'completed').length;
  const inProgressCount = summary?.in_progress_steps ?? sortedSteps.filter((s) => s.status === 'in_progress').length;
  const skippedCount = summary?.skipped_steps ?? sortedSteps.filter((s) => s.status === 'skipped').length;
  const failedCount = summary?.failed_steps ?? sortedSteps.filter((s) => s.status === 'failed').length;
  const pendingCount = totalSteps - completedCount - inProgressCount - skippedCount - failedCount;
  const completionPct = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  const hasSessionInfo = enrollment.event_name || enrollment.session_name || enrollment.course_name;

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-[780px] mx-auto px-6 pt-5 pb-16">
        {/* ── Back link ── */}
        {magicToken && (
          <Link
            to={`/training/portal?t=${magicToken}`}
            className="inline-flex items-center gap-1 text-[13px] font-medium text-gray-500 hover:text-indigo-600 mb-4 no-underline transition-colors"
          >
            <CaretLeft size={14} weight="bold" /> My Registrations
          </Link>
        )}

        {/* ── Session Detail Header ── */}
        {hasSessionInfo && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 overflow-hidden">
            {/* Top accent bar */}
            <div className="h-1 bg-gradient-to-r from-indigo-500 via-blue-500 to-emerald-500" />
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h1 className="text-lg font-bold text-gray-900 leading-snug">
                    {enrollment.course_name || enrollment.event_name || 'Training Session'}
                  </h1>
                  {enrollment.event_name && enrollment.course_name && enrollment.event_name !== enrollment.course_name && (
                    <p className="text-sm text-gray-500 mt-0.5">{enrollment.event_name}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Badge variant="outline" className={enrollmentCfg.cls}>
                    <EnrollmentIcon size={12} weight="fill" className="mr-1" />
                    {enrollmentCfg.label}
                  </Badge>
                  {enrollment.event_mode && (
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-50">
                      <Monitor size={12} weight="bold" className="mr-1" />
                      {getModeLabel(enrollment.event_mode)}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Session meta row */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-3 text-xs text-gray-500">
                {enrollment.session_name && (
                  <span className="flex items-center gap-1.5">
                    <Buildings size={13} weight="bold" className="text-gray-400" />
                    {enrollment.session_name}
                  </span>
                )}
                {(enrollment.session_start_date || enrollment.session_end_date) && (
                  <span className="flex items-center gap-1.5">
                    <CalendarBlank size={13} weight="bold" className="text-gray-400" />
                    {formatDate(enrollment.session_start_date)}
                    {enrollment.session_end_date && enrollment.session_end_date !== enrollment.session_start_date && (
                      <> &rarr; {formatDate(enrollment.session_end_date)}</>
                    )}
                  </span>
                )}
                {(enrollment.session_start_time || enrollment.session_end_time) && (
                  <span className="flex items-center gap-1.5">
                    <Clock size={13} weight="bold" className="text-gray-400" />
                    {formatTime(enrollment.session_start_time)}
                    {enrollment.session_end_time && <> - {formatTime(enrollment.session_end_time)}</>}
                  </span>
                )}
                {enrollment.location_name && (
                  <span className="flex items-center gap-1.5">
                    <MapPin size={13} weight="bold" className="text-gray-400" />
                    {enrollment.location_name}
                  </span>
                )}
                {enrollment.registered_on && (
                  <span className="flex items-center gap-1.5">
                    <CheckCircle size={13} weight="fill" className="text-emerald-400" />
                    Registered {formatDate(enrollment.registered_on)}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Progress Summary ── */}
        {totalSteps > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
            <div className="flex flex-wrap gap-5 mb-3">
              {[
                { value: pendingCount, label: 'Pending', dot: 'bg-gray-300' },
                { value: completedCount, label: 'Completed', dot: 'bg-emerald-500' },
                ...(inProgressCount > 0 ? [{ value: inProgressCount, label: 'In Progress', dot: 'bg-blue-500' }] : []),
                ...(skippedCount > 0 ? [{ value: skippedCount, label: 'Skipped', dot: 'bg-amber-400' }] : []),
                ...(failedCount > 0 ? [{ value: failedCount, label: 'Failed', dot: 'bg-red-500' }] : []),
                { value: totalSteps, label: 'Total Steps', dot: 'bg-indigo-400' },
              ].map(({ value, label, dot }) => (
                <div key={label} className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
                    <span className="text-2xl font-bold text-gray-800">{value}</span>
                  </div>
                  <span className="text-xs text-gray-500">{label}</span>
                </div>
              ))}
              <div className="flex flex-col gap-0.5">
                <span className="text-2xl font-bold text-emerald-600">{completionPct}%</span>
                <span className="text-xs text-gray-500">Progress</span>
              </div>
            </div>

            {/* Progress bar */}
            <Progress value={completionPct} className="h-2" />

            {/* Legend */}
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Completed</span>
              {skippedCount > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-300" />Skipped</span>}
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-200" />Remaining</span>
            </div>
          </div>
        )}

        {/* ── Attendance Summary ── */}
        {detail.session_days && detail.session_days.length > 0 && (
          <div className="mb-4">
            <SectionLabel label="Session Schedule" count={detail.session_days.length} />
            <div className="space-y-2">
              {detail.session_days.map((day, i) => (
                <SessionDayCard key={day.id} day={day} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* ── Steps Timeline ── */}
        {sortedSteps.length > 0 ? (
          <div>
            <SectionLabel label="Course Steps" count={sortedSteps.length} />
            {sortedSteps.map((step, i) => (
              <StepTimelineItem
                key={step.id}
                step={step}
                index={i}
                isLast={i === sortedSteps.length - 1}
                isUpdating={updatingStep === step.id}
                onStart={handleStartStep}
                onComplete={handleCompleteStep}
                onOpenContent={handleOpenContent}
                onSubmitWork={(id, name) => {
                  setSubmitUrl(''); setSubmitNotes('');
                  setSubmitModal({ progressId: id, stepName: name });
                }}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
            <BookOpen size={40} weight="duotone" className="text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-700 mb-1.5">No steps yet</h3>
            <p className="text-[13px] text-gray-500">
              Course steps will appear here once they are added by the trainer.
            </p>
          </div>
        )}
      </div>

      {/* ── Step Player Modal ── */}
      {playerStep && (
        <StepPlayerModal
          step={playerStep}
          token={magicToken}
          onClose={() => { setPlayerStep(null); loadData(); }}
          onStepUpdated={loadData}
        />
      )}

      {/* ── Submit Work Dialog ── */}
      <Dialog open={!!submitModal} onOpenChange={(open) => { if (!open && !submitting) { setSubmitModal(null); } }}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Submit Work</DialogTitle>
            {submitModal && (
              <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">{submitModal.stepName}</p>
            )}
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-1">
            <div>
              <Label className="text-xs font-semibold mb-1 flex items-center gap-1">
                <LinkSimple size={12} weight="bold" />
                Submission URL
              </Label>
              <Input
                value={submitUrl}
                onChange={(e) => setSubmitUrl(e.target.value)}
                placeholder="https://docs.google.com/... or file link"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1">Notes (optional)</Label>
              <Textarea
                value={submitNotes}
                onChange={(e) => setSubmitNotes(e.target.value)}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitModal(null)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitWork}
              disabled={submitting || !submitUrl.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {submitting
                ? <><ArrowClockwise size={14} weight="bold" className="animate-spin mr-1.5" /> Submitting...</>
                : <><PaperPlaneTilt size={14} weight="fill" className="mr-1.5" /> Submit</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Step Timeline Item ────────────────────────────────────────────────────

function StepTimelineItem({
  step, index, isLast, isUpdating, onStart, onComplete, onOpenContent, onSubmitWork,
}: {
  step: StepProgress;
  index: number;
  isLast: boolean;
  isUpdating: boolean;
  onStart: (id: number) => void;
  onComplete: (id: number) => void;
  onOpenContent: (step: StepProgress) => void;
  onSubmitWork?: (progressId: number, stepName: string) => void;
}) {
  const cfg = getStepStatusCfg(step.status);
  const NodeIcon = cfg.nodeIcon;
  const TypeIcon = getTypeIcon(step.step_type || '');

  const isCompleted = step.status === 'completed';
  const isSkipped = step.status === 'skipped';
  const isInProgress = step.status === 'in_progress';
  const isNotStarted = step.status === 'not_started';
  const isFailed = step.status === 'failed';

  const hasContent = !!getContentUrl(step.step_attachment);
  const contentBtnLabel = isCompleted ? 'Review' : isInProgress ? 'Continue' : 'Start';
  const isSubmittable = ['task', 'assessment'].includes((step.step_type || '').toLowerCase());

  return (
    <div className="flex gap-3">
      {/* Timeline column */}
      <div className="flex flex-col items-center flex-shrink-0 w-7 pt-3">
        <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center z-10 flex-shrink-0
          ${cfg.nodeBorder} ${isCompleted || isSkipped || isInProgress || isFailed ? cfg.nodeBg : 'bg-white'}`}
        >
          {NodeIcon ? (
            <NodeIcon size={14} weight="fill" className="text-white" />
          ) : (
            <span className="text-[10px] font-bold text-gray-400">{index + 1}</span>
          )}
        </div>
        {!isLast && <div className={`w-0.5 flex-1 mt-1 min-h-[20px] ${cfg.connectorBg}`} />}
      </div>

      {/* Card */}
      <div className={`flex-1 ${isLast ? 'pb-2' : 'pb-4'}`}>
        <div className={`rounded-xl border border-gray-200 border-l-4 shadow-sm overflow-hidden transition-all
          ${cfg.cardBorder} ${cfg.cardBg}
          ${isSkipped ? 'opacity-70' : ''}
          ${!isCompleted && !isSkipped ? 'hover:shadow-md' : ''}`}
        >
          <div className="p-4">
            {/* Top row */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5 flex-1 min-w-0">
                <span className={`flex-shrink-0 min-w-[1.5rem] h-5 rounded-full text-[11px] font-semibold flex items-center justify-center px-1.5 mt-0.5 ${cfg.numberCls}`}>
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className={`text-sm font-semibold leading-snug ${isSkipped ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                    {step.step_name || 'Untitled Step'}
                  </h3>
                  {step.step_description && (
                    <p className={`text-xs mt-0.5 line-clamp-2 leading-relaxed ${isSkipped ? 'text-gray-400' : 'text-gray-500'}`}>
                      {step.step_description}
                    </p>
                  )}
                </div>
              </div>

              {/* Badges */}
              <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                {step.step_type && (
                  <Badge variant="outline" className={getTypeCls(step.step_type)}>
                    <TypeIcon size={11} weight="bold" className="mr-1" />
                    {step.step_type}
                  </Badge>
                )}
                <Badge variant="outline" className={cfg.badgeCls}>
                  {NodeIcon ? <NodeIcon size={11} weight="fill" className="mr-1" /> : <Circle size={11} weight="fill" className="mr-1" />}
                  {cfg.label}
                </Badge>
              </div>
            </div>

            {/* Date row */}
            {(step.step_start_date || step.step_due_date) && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-2.5 pl-7">
                <div className="flex items-center gap-1">
                  <CalendarBlank size={13} weight="bold" className="text-gray-400" />
                  <span>
                    {formatDate(step.step_start_date)}
                    {step.step_due_date && step.step_due_date !== step.step_start_date && (
                      <> &rarr; {formatDate(step.step_due_date)}</>
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Progress bar for in-progress */}
            {isInProgress && (step.progress_percentage ?? 0) > 0 && (
              <div className="mt-2.5 pl-7">
                <div className="flex items-center gap-2">
                  <Progress value={step.progress_percentage ?? 0} className="h-1.5 flex-1" />
                  <span className="text-[11px] font-medium text-blue-600 tabular-nums w-8 text-right">{step.progress_percentage}%</span>
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">Continue from where you left off</p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2 mt-3 pl-7 flex-wrap">
              {hasContent && !isSkipped && (
                <Button
                  size="sm"
                  variant={isCompleted ? 'outline' : 'default'}
                  onClick={() => onOpenContent(step)}
                  disabled={isUpdating}
                  className={isCompleted ? '' : 'bg-indigo-600 hover:bg-indigo-700'}
                >
                  {isCompleted ? <Eye size={13} weight="bold" className="mr-1.5" /> : <Play size={13} weight="fill" className="mr-1.5" />}
                  {contentBtnLabel}
                </Button>
              )}

              {isNotStarted && !hasContent && (
                <Button
                  size="sm"
                  onClick={() => onStart(step.id)}
                  disabled={isUpdating}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {isUpdating ? <ArrowClockwise size={13} weight="bold" className="animate-spin mr-1.5" /> : <Play size={13} weight="fill" className="mr-1.5" />}
                  Start
                </Button>
              )}

              {isInProgress && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onComplete(step.id)}
                  disabled={isUpdating}
                  className="border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                >
                  {isUpdating ? <ArrowClockwise size={13} weight="bold" className="animate-spin mr-1.5" /> : <CheckCircle size={13} weight="fill" className="mr-1.5" />}
                  Mark Complete
                </Button>
              )}

              {onSubmitWork && isSubmittable && !isCompleted && !isSkipped && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onSubmitWork(step.id, step.step_name || 'Submit Work')}
                  disabled={isUpdating}
                  className="border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                >
                  <PaperPlaneTilt size={13} weight="fill" className="mr-1.5" />
                  Submit Work
                </Button>
              )}

              {isFailed && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onStart(step.id)}
                  disabled={isUpdating}
                  className="border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                >
                  <ArrowClockwise size={13} weight="bold" className="mr-1.5" />
                  Retry
                </Button>
              )}
            </div>

            {isSkipped && (
              <div className="mt-2.5 pl-7">
                <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-50">
                  <SkipForward size={11} weight="bold" className="mr-1" />
                  This step was skipped
                </Badge>
              </div>
            )}

            {isCompleted && step.completed_at && (
              <div className="mt-2 pl-7">
                <span className="text-[11px] text-gray-400">Completed on {formatDate(step.completed_at)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
